import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/local.js';
import { readEncryptionState, readUnlockedBy } from '../crypto/e2e.js';
import type { EncryptionState } from '../crypto/e2e.js';
import {
  addThisPasskey,
  beginSetup,
  KeySetupError,
  unlockWithCode,
  unlockWithPasskey,
} from '../crypto/keySetup.js';
import type { KeyError, PendingSetup } from '../crypto/keySetup.js';
import { readHouseholdsPending } from '../crypto/householdKeys.js';

function reasonOf(error: unknown): KeyError {
  return error instanceof KeySetupError ? error.reason : 'offline';
}

/**
 * Ende-zu-Ende (E21, 25.10.2026) in den Einstellungen: einrichten, Stand
 * zeigen, ein Gerät entsperren. Der Wiederherstellungscode erscheint genau
 * einmal — weiter geht es erst, wenn er gesichert ist.
 */
export function EncryptionSetting(): React.JSX.Element {
  const { t } = useTranslation();
  const state = useLiveQuery(() => readEncryptionState(db), [], 'off' as EncryptionState);
  const unlockedBy = useLiveQuery(() => readUnlockedBy(db), [], null);
  const householdsWaiting = useLiveQuery(() => readHouseholdsPending(db), [], 0);
  const [added, setAdded] = useState(false);
  const [pending, setPending] = useState<PendingSetup | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<KeyError | null>(null);
  const [copied, setCopied] = useState(false);
  const savedId = useId();

  async function run(step: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (failure) {
      setError(reasonOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="e2e-heading" className="kw-tile flex flex-col gap-3">
      <h3 id="e2e-heading" className="kw-kicker">
        {t('e2e.heading')}
      </h3>

      {pending ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm">{t('e2e.codeIntro')}</p>
          <p
            className="rounded-xl border border-line p-3 text-center font-mono text-lg tracking-wider break-all select-all"
            aria-label={t('e2e.codeLabel')}
          >
            {pending.code}
          </p>
          <button
            type="button"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(pending.code)
                .then(() => setCopied(true))
                .catch(() => setCopied(false))
            }
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {copied ? t('e2e.copied') : t('e2e.copy')}
          </button>
          <p className="text-xs text-warn">{t('e2e.codeWarning')}</p>
          <label htmlFor={savedId} className="flex items-start gap-3 text-sm">
            <input
              id={savedId}
              type="checkbox"
              checked={saved}
              onChange={(event) => setSaved(event.target.checked)}
              className="mt-1 size-5 shrink-0 accent-accent"
            />
            {t('e2e.codeSaved')}
          </label>
          <button
            type="button"
            disabled={!saved || busy}
            onClick={() =>
              void run(async () => {
                await pending.finish();
                setPending(null);
              })
            }
            className="kw-btn kw-btn-small"
          >
            {busy ? t('e2e.working') : t('e2e.finish')}
          </button>
        </div>
      ) : state === 'ready' ? (
        <>
          <p className="text-sm text-good">{t('e2e.ready')}</p>
          <p className="text-xs text-ink-muted">{t('e2e.readyHint')}</p>
          {householdsWaiting > 0 ? (
            <p className="text-xs text-warn">{t('e2e.householdWaiting')}</p>
          ) : null}
          {added ? (
            <p role="status" className="text-sm text-good">
              {t('e2e.passkeyAdded')}
            </p>
          ) : null}
          {/* Nur nach dem Code fehlt hier Face ID. */}
          {unlockedBy === 'code' ? (
            <>
              <p className="text-xs text-ink-muted">{t('e2e.addPasskeyHint')}</p>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await addThisPasskey();
                    setAdded(true);
                  })
                }
                className="kw-btn kw-btn-quiet kw-btn-small"
              >
                {busy ? t('e2e.working') : t('e2e.addPasskey')}
              </button>
            </>
          ) : null}
        </>
      ) : state === 'locked' ? (
        <UnlockEncryption />
      ) : (
        <>
          <p className="text-sm">{t('e2e.intro')}</p>
          <p className="text-xs text-ink-muted">{t('e2e.introHint')}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                setSaved(false);
                setCopied(false);
                setPending(await beginSetup());
              })
            }
            className="kw-btn kw-btn-small"
          >
            {busy ? t('e2e.working') : t('e2e.setup')}
          </button>
        </>
      )}

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {t(`e2e.error.${error}`)}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Gerät entsperren: Face ID, sonst Wiederherstellungscode. Steht in den
 * Einstellungen und überall dort, wo Verschlossenes wartet (Tagebuch).
 */
export function UnlockEncryption(): React.JSX.Element {
  const { t } = useTranslation();
  const codeId = useId();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<KeyError | null>(null);

  async function run(step: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (failure) {
      setError(reasonOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm">{t('e2e.locked')}</p>
      <button
        type="button"
        disabled={busy}
        onClick={() => void run(() => unlockWithPasskey())}
        className="kw-btn kw-btn-small"
      >
        {t('e2e.unlockFaceId')}
      </button>
      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void run(() => unlockWithCode(code));
        }}
      >
        <label htmlFor={codeId} className="text-xs text-ink-muted">
          {t('e2e.codeInput')}
        </label>
        <input
          id={codeId}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="kw-field font-mono"
        />
        <button
          type="submit"
          disabled={busy || code.trim() === ''}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('e2e.unlockCode')}
        </button>
      </form>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {t(`e2e.error.${error}`)}
        </p>
      ) : null}
    </div>
  );
}
